import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CreatePurpose, type UpdatePurpose } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetPurposesQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['purposes'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/purposes')
      if (error) throwOnError(error)
      return data
    }
  })
}

export const useCreatePurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (purpose: CreatePurpose) => {
      const { data, error } = await authFetch().POST('/purposes', {
        body: purpose
      })
      if (error) throwOnError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purposes'] })
    }
  })
}

export const useUpdatePurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      purposeId,
      etag,
      body
    }: {
      purposeId: number
      etag: string
      body: UpdatePurpose
    }) => {
      const { data, error } = await authFetch().PUT('/purposes/{purposeId}', {
        params: {
          path: { purposeId },
          header: { 'If-Match': etag }
        },
        body
      })
      if (error) throwOnError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purposes'] })
    }
  })
}

export const useDeletePurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (purposeId: number) =>
      await authFetch().DELETE('/purposes/{purposeId}', {
        params: { path: { purposeId } }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purposes'] })
    }
  })
}
